import { useState, useEffect } from 'react';
import { Eye, Users, MapPin, Clock, BarChart3, Globe, Monitor, Smartphone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { apiRequest, formatDate } from '../../lib/utils';

export default function AdminAnalytics() {
  const [stats, setStats] = useState({
    total_visits: 0,
    total_leads: 0,
    total_appointments: 0,
    visits_by_city: [],
    page_views: [],
  });
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiRequest('/admin/analytics/summary'),
      apiRequest('/admin/visits'),
    ])
      .then(([statsData, visitsData]) => {
        setStats(statsData);
        setVisits(visitsData);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-analytics">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
          Visitor Analytics
        </h1>
        <p className="text-slate-500 mt-1">Track visitor behavior and engagement</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        <Card className="border-none shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Visits</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_visits}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Leads</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_leads}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Conversion Rate</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.total_visits > 0 
                    ? ((stats.total_leads / stats.total_visits) * 100).toFixed(1) 
                    : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Top Pages */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-orange-600" />
              Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.page_views && stats.page_views.length > 0 ? (
              <div className="space-y-4">
                {stats.page_views.map((page, idx) => {
                  const maxCount = stats.page_views[0]?.count || 1;
                  const percentage = (page.count / maxCount) * 100;
                  return (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-600 truncate max-w-[200px]">
                          {page._id || '/'}
                        </span>
                        <span className="text-sm font-medium text-slate-900">{page.count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-600 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No page view data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Visits by City */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-600" />
              Visits by Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.visits_by_city && stats.visits_by_city.length > 0 ? (
              <div className="space-y-4">
                {stats.visits_by_city.map((city, idx) => {
                  const maxCount = stats.visits_by_city[0]?.count || 1;
                  const percentage = (city.count / maxCount) * 100;
                  return (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-600">{city._id || 'Unknown'}</span>
                        <span className="text-sm font-medium text-slate-900">{city.count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No location data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Visits Table */}
      <Card className="border-none shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Recent Visits</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {visits.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500">No visit data yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Pages Viewed</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visits.slice(0, 20).map((visit) => (
                    <TableRow key={visit.id}>
                      <TableCell className="font-mono text-sm">{visit.ip_address}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          <span className="text-sm">
                            {visit.geo_location?.city || 'Unknown'}
                            {visit.geo_location?.regionName && `, ${visit.geo_location.regionName}`}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {visit.device?.includes('Mobile') ? (
                            <Smartphone className="w-4 h-4 text-slate-400" />
                          ) : (
                            <Monitor className="w-4 h-4 text-slate-400" />
                          )}
                          <span className="text-sm text-slate-600 truncate max-w-[150px]">
                            {visit.device || 'Unknown'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {visit.pages_viewed?.length || 0} pages
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="text-sm text-slate-600">
                            {Math.round((visit.time_spent || 0) / 60)}m
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatDate(visit.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
